/**
 * PartyPicker — inline person/company selector (epic #833, B1)
 *
 * The party-native replacement for the legacy customer autocomplete: a
 * person/company toggle, a debounced registry search and quick-create buttons
 * that reuse the person/company form dialogs. Used by the work-order create
 * wizard; reusable anywhere a party (orderer, owner, …) is picked.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { usePersons, type Person } from '@/hooks/usePersons';
import { useCompanies, type Company } from '@/hooks/useCompanies';
import { useDebounce } from '@/hooks/useDebounce';

export type PartyType = 'person' | 'company';

/** A selected party reference with display fields. */
export interface PartyRef {
  partyType: PartyType;
  personId: string | null;
  companyId: string | null;
  name: string;
  phone: string | null;
}

export function personToPartyRef(person: Person): PartyRef {
  return {
    partyType: 'person',
    personId: person.id,
    companyId: null,
    name: `${person.firstName} ${person.lastName}`.trim(),
    phone: person.phone ?? null,
  };
}

export function companyToPartyRef(company: Company): PartyRef {
  return {
    partyType: 'company',
    personId: null,
    companyId: company.id,
    name: company.name,
    phone: company.phone ?? null,
  };
}

interface PartyPickerProps {
  onSelect: (party: PartyRef) => void;
  autoFocus?: boolean;
  /** Restrict which party types can be picked (default: both). */
  allowedTypes?: readonly PartyType[];
}

const BOTH_PARTY_TYPES: readonly PartyType[] = ['person', 'company'];

export function PartyPicker({ onSelect, autoFocus, allowedTypes = BOTH_PARTY_TYPES }: PartyPickerProps) {
  const { t } = useTranslation('workorders');

  const [partyType, setPartyType] = useState<PartyType>(allowedTypes[0] ?? 'person');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [personQuickOpen, setPersonQuickOpen] = useState(false);
  const [companyQuickOpen, setCompanyQuickOpen] = useState(false);

  const enableSearch = debouncedSearch.length >= 2;
  const personsQuery = usePersons(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType === 'person' },
  );
  const companiesQuery = useCompanies(
    { q: debouncedSearch || undefined },
    { enabled: enableSearch && partyType === 'company' },
  );
  const personOptions = personsQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const companyOptions = companiesQuery.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <Stack spacing={1.5}>
      {allowedTypes.length > 1 && (
        <ToggleButtonGroup
          value={partyType}
          exclusive
          fullWidth
          size="small"
          onChange={(_e, value: PartyType | null) => {
            if (value) {
              setPartyType(value);
              setSearch('');
            }
          }}
        >
          <ToggleButton value="person">{t('parties.person')}</ToggleButton>
          <ToggleButton value="company">{t('parties.company')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      {partyType === 'person' ? (
        <Autocomplete<Person>
          options={personOptions}
          loading={personsQuery.isLoading}
          value={null}
          onChange={(_e, value) => {
            if (value) onSelect(personToPartyRef(value));
          }}
          inputValue={search}
          onInputChange={(_e, value) => setSearch(value)}
          getOptionLabel={(o) => `${o.firstName} ${o.lastName}`}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(x) => x}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.firstName} {option.lastName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.phone, option.email].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('parties.selectPerson')}
              autoFocus={autoFocus}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {personsQuery.isLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          noOptionsText={enableSearch ? t('create.noPartiesFound') : t('create.partySearchHint')}
        />
      ) : (
        <Autocomplete<Company>
          options={companyOptions}
          loading={companiesQuery.isLoading}
          value={null}
          onChange={(_e, value) => {
            if (value) onSelect(companyToPartyRef(value));
          }}
          inputValue={search}
          onInputChange={(_e, value) => setSearch(value)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          filterOptions={(x) => x}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.businessId, option.phone].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('parties.selectCompany')}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {companiesQuery.isLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          noOptionsText={enableSearch ? t('create.noPartiesFound') : t('create.partySearchHint')}
        />
      )}

      <Box>
        {partyType === 'person' ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setPersonQuickOpen(true)}>
            {t('parties.quickCreatePerson')}
          </Button>
        ) : (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCompanyQuickOpen(true)}>
            {t('parties.quickCreateCompany')}
          </Button>
        )}
      </Box>

      <PersonFormDialog
        open={personQuickOpen}
        onClose={() => setPersonQuickOpen(false)}
        onCreated={(person) => onSelect(personToPartyRef(person))}
        // Quick create stays short; address/notes sit behind Lisätiedot (#2904).
        showExtended={false}
      />
      <CompanyFormDialog
        open={companyQuickOpen}
        onClose={() => setCompanyQuickOpen(false)}
        onCreated={(company) => onSelect(companyToPartyRef(company))}
      />
    </Stack>
  );
}
